/**
 * Inventory quick-action tile.
 *
 * Ported from ipd/utils/IpdQuickActionCard (untouched — shared with the rest of the
 * app). The one substantive change: the original hardcoded `<Grid item xs={12} sm={6}
 * md={4}>` inside the leaf with no lg, so callers could not choose their own column
 * count and every action grid was stuck at 3-up. Breakpoints are props now.
 */
import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IPD_COLORS, INV_RADIUS, INV_TYPE } from '../theme';

/** 12-col spans so N tiles fill a row instead of leaving a ragged gap. */
export function quickActionBreakpoints(count) {
  const cols = count >= 4 ? 3 : count === 3 ? 4 : count === 2 ? 6 : 12;
  return { xs: 12, sm: count === 1 ? 12 : 6, md: cols, lg: cols };
}

export default function QuickActionCard({
  icon: Icon,
  title,
  description,
  onClick,
  xs = 12,
  sm = 6,
  md = 4,
  lg = 3,
}) {
  return (
    <Grid item xs={xs} sm={sm} md={md} lg={lg}>
      <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 2,
          borderRadius: `${INV_RADIUS.tile}px`,
          border: `1px solid ${IPD_COLORS.border}`,
          backgroundColor: IPD_COLORS.cardBg,
          color: IPD_COLORS.textPrimary,
          boxShadow: IPD_COLORS.shadow.card,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            backgroundColor: IPD_COLORS.primary,
            color: '#FFFFFF',
            borderColor: IPD_COLORS.primary,
            boxShadow: IPD_COLORS.shadow.cardHover,
            '& .qa-icon': { color: '#FFFFFF !important' },
            '& .qa-title': { color: '#FFFFFF !important' },
            '& .qa-desc': { color: 'rgba(255,255,255,0.85) !important' },
            '& .qa-arrow': { color: '#FFFFFF !important' },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          {Icon && (
            <Icon className="qa-icon" sx={{ fontSize: 32, color: IPD_COLORS.primary, transition: 'color 0.3s ease' }} />
          )}
          <ArrowForwardIcon
            className="qa-arrow"
            sx={{ fontSize: 20, color: IPD_COLORS.textSecondary, transition: 'color 0.3s ease' }}
          />
        </Box>
        <Typography className="qa-title" sx={{ ...INV_TYPE.cardTitle, mb: 0.5, transition: 'color 0.3s ease' }}>
          {title}
        </Typography>
        {description && (
          <Typography
            className="qa-desc"
            sx={{ ...INV_TYPE.caption, color: IPD_COLORS.textSecondary, transition: 'color 0.3s ease' }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Grid>
  );
}
